import {z} from 'zod';

import {Locale} from '@dms/api/base';
import {
  EntityResourceIds,
  MessageContentFormat,
  PhoneNumber,
  RecordIdHttpModel,
  UuidResponseBody,
} from '@dms/api/shared';

import {LiteralUnionAutocomplete, ValueOf} from 'shared/web';

import {type BulkOperationResponseBody, type BulkOperationState} from '../bulkOperation/types';
import {ACCOUNTING_SUBJECT_VOCABULARY} from './constants/ACCOUNTING_SUBJECT_VOCABULARY';

export type SecondaryRecordId = {
  resourceId: EntityResourceIds;
  recordId: string;
};

export type PatchSentToastNotificationApiResponse = SentToastNotificationResponseBody;
export type PatchSentToastNotificationApiArg = {
  id: string;
  body: PatchSentToastNotificationRequestBody;
};

export type GetSentToastNotificationApiResponse = SentToastNotificationResponseBody;
export type GetSentToastNotificationApiArg = {
  id: string;
};

export type ListSentToastNotificationsApiResponse = ListSentToastNotificationsResponseBody;
export type ListSentToastNotificationsApiArg = {
  limit?: number;
  after?: string;
};

export type ListSentToastNotificationsResponseBody = {
  notifications: SentToastNotificationResponseBody[];
  count: number;
  hasMoreNotifications: boolean;
};

export type MessagingAllowlistChannel = 'email' | 'sms';

export type MessagingAllowlistResponseBody = {
  enabled: boolean;
  patterns: string[];
};

export type UpdateMessagingAllowlistRequestBody = {
  enabled: boolean;
  patterns: string[];
};

export type GetMessagingAllowlistApiArg = {
  channel: MessagingAllowlistChannel;
};
export type GetMessagingAllowlistApiResponse = MessagingAllowlistResponseBody;

export type UpdateMessagingAllowlistApiArg = {
  channel: MessagingAllowlistChannel;
  body: UpdateMessagingAllowlistRequestBody;
};
export type UpdateMessagingAllowlistApiResponse = /** status 204  */ void;

export type SendCustomerEmailApiResponse = /** status 204  */ void;
export type SendCustomerEmailApiArg = {
  /** User JWT tokens are allowed */
  authorization?: string;
  sendCustomerEmailRequestBody: SendCustomerEmailRequestBody;
};

export type BusinessCaseOnlineOfferPayloadRequestBody = {
  businessCaseId: string;
  vehicleId: string;
  photoId: string | null;
  sellerId: string;
  onlineOfferUri: string;
  body: string;
};
export type AttachmentRequestBody = {
  name: string;
  url: string;
};
export type ServiceCheckInPayloadRequestBody = {
  serviceCaseId: string;
  digitalCertificateUrl: string;
  attachments: AttachmentRequestBody[];
};
export type ServiceHandoverPayloadRequestBody = {
  serviceCaseId: string;
  digitalCertificateUrl: string;
  attachments: AttachmentRequestBody[];
};
export type GeneralCustomerMessagePayloadRequestBody = {
  subject: string;
  body: string;
};
export type CustomerMessageRequestBody = {
  customerId: string;
  customerMessageId:
    | 'business_case-online_offer'
    | 'customer-message'
    | 'service-customer_check_in'
    | 'service-customer_handover';
  payload:
    | BusinessCaseOnlineOfferPayloadRequestBody
    | ServiceCheckInPayloadRequestBody
    | ServiceHandoverPayloadRequestBody
    | GeneralCustomerMessagePayloadRequestBody;
  locale?: string | null;
};
export type SendCustomerEmailAttachmentRequestBody = {
  fileId: string;
  convertToPdf?: boolean;
};
export type SendCustomerEmailRequestBody = {
  subject: string;
  body: string;
  bodyFormat?: MessageContentFormat;
  customerId: string;
  customerEmailAddress: string;
  attachments?: SendCustomerEmailAttachmentRequestBody[];
  recordId: string | null;
  smtpCredentialsId: string;
  copyTo?: string[];
  resourceId: EntityResourceIds | null;
  secondaryRecordIds?: SecondaryRecordId[];
};

export type SendCustomerEmailReplyRequestBody = {
  emailMessageId: string;
  body: string;
  bodyFormat?: MessageContentFormat;
  customerId: string;
  customerEmailAddress: string;
  attachments?: SendCustomerEmailAttachmentRequestBody[];
  recordId?: string | null;
  resourceId?: EntityResourceIds | null;
  copyTo?: string[];
  secondaryRecordIds?: SecondaryRecordId[];
  emailCredentialsId?: string | null;
  subject?: string | null;
};
export type SendCustomerEmailReplyApiResponse = /** status 204 */ void;
export type SendCustomerEmailReplyApiArg = {
  sendCustomerEmailReplyRequestBody: SendCustomerEmailReplyRequestBody;
};

export type RenderCustomerEmailReplyRequestBody = {
  emailMessageId: string;
  body: string;
  bodyFormat?: MessageContentFormat;
  customerId: string;
  subject?: string | null;
};
export const renderCustomerEmailReplyResponseBodySchema = z.object({
  subject: z.string(),
  body: z.string(),
});
export type RenderCustomerEmailReplyResponseBody = z.infer<
  typeof renderCustomerEmailReplyResponseBodySchema
>;
export type RenderCustomerEmailReplyApiResponse =
  /** status 200 */ RenderCustomerEmailReplyResponseBody;
export type RenderCustomerEmailReplyApiArg = {
  renderCustomerEmailReplyRequestBody: RenderCustomerEmailReplyRequestBody;
};

export type PatchSentToastNotificationRequestBody = {
  isRead?: boolean | null;
};

export type PatchUnreadSentToastNotificationsApiArg = {
  body: PatchSentToastNotificationRequestBody;
};

export type SentToastNotificationResponseBody =
  | ({
      type: 'batch_download-bulk_event_state_changed';
    } & BatchDownloadBulkEventStateChanged)
  | ({
      type: 'bulk_operation-state_changed';
    } & ToastNotificationBulkOperationStateChanged)
  | ({
      type: 'bulk_operation-records_changed';
    } & ToastNotificationBulkOperationRecordsChanged)
  | ({
      type: 'bulk_operation-progress_changed';
    } & BackgroundNotificationBulkOperationProgressChanged)
  | ({
      type: 'participation-assignee_assigned';
    } & ParticipationAssigneeAssigned)
  | ({
      type: 'participation-assignee_unassigned';
    } & ParticipationAssigneeUnassigned)
  | ({
      type: 'participation-owner_changed';
    } & ParticipationOwnerChanged)
  | ({
      type: 'participation-owner_assigned';
    } & ToastNotificationParticipationOwnerAssigned)
  | ({
      type: 'participation-owner_unassigned';
    } & ToastNotificationParticipationOwnerUnassigned)
  | ({
      type: 'vehicle_photo-rotate_finished';
    } & VehicleFileOperationFinished)
  | ({
      type: 'vehicle_photo-remove_background_finished';
    } & VehicleFileOperationFinished)
  | ({
      type: 'vehicle_photo-copy_finished';
    } & VehiclePhotoCopyFinished)
  | ({
      type: 'vehicle-file_operation_finished';
    } & VehicleFileOperationFinished)
  | ({
      type: 'vehicle-inspection_expiration_notice';
    } & VehicleInspectionExpirationNotice)
  | ({
      type: 'vehicle-next_service_notice';
    } & VehicleNextServiceNotice)
  | ({
      type: 'orchestration-rule_triggered';
    } & ToastNotificationAutomationRuleTriggered)
  | ({
      type: 'orchestration-rule_ran_for_record';
    } & ToastNotificationAutomationRuleTriggered)
  | ({
      type: 'orchestration-rule_ran_for_team';
    } & ToastNotificationAutomationRuleTriggered)
  | ({
      type: 'orchestration-rule_notified_you';
    } & ToastNotificationAutomationRuleTriggered)
  | ({
      type: 'orchestration-run_awaiting_confirmation';
    } & ToastNotificationAutomationRunAwaitingConfirmation)
  | ({
      type: 'orchestration-run_stopped';
    } & ToastNotificationAutomationRunStopped)
  | ({
      type: 'orchestration-rule_approval_requested';
    } & ToastNotificationAutomationRuleApprovalRequested)
  | ({
      type: 'comment-mention';
    } & CommentMention)
  | ({
      type: 'comment-record_owner';
    } & CommentRecordOwner)
  | ({
      type: 'new_vehicle-factory_confirmation';
    } & ToastNotificationNewVehicleFactoryConfirmation)
  | ({
      type: 'new_vehicle-factory_vin_conflict';
    } & ToastNotificationNewVehicleFactoryVinConflict)
  | ({
      type: 'sale_vehicle-reservation_cancelled';
    } & SaleVehicleReservationCancelled)
  | ({
      type: 'access_control-user_permission_changed';
    } & PermissionChanged)
  | ({
      type: 'datagrid-custom_preset_shared';
    } & PresetChanged)
  | ({
      type: 'datagrid-data_export';
    } & DataGridExport)
  | ({
      type: 'sale_vehicle_photo-remove_background_finished';
    } & SaleVehiclePhotoRemoveBackgroundFinished)
  | ({
      type: 'file_storage-file_uploaded';
    } & FileStorageFileUploadedNotification)
  | ({
      type: 'file_storage-file_variant_uploaded';
    } & FileStorageFileVariantUploadedNotification)
  | ({
      type: 'vehicle-days_since_last_reprice_notice';
    } & VehicleDaysSinceLastRepriceNotification)
  | ({
      type: 'digital_signature-document_signed';
    } & DigitalDocumentSignedNotification)
  | ({
      type: 'vehicle-warranty_expiration_notice';
    } & VehicleWarrantyExpirationNotification)
  | ({
      type: 'vehicle-unregistered_notice';
    } & ToastNotificationVehicleUnregisteredNotice)
  | ({
      type: 'task_management-task_created';
    } & TaskCreatedNotification)
  | ({
      type: 'task_management-task_updated';
    } & TaskUpdatedNotification)
  | ({
      type: 'task_management-due_date_notice';
    } & TaskDueNoticeNotification)
  | ({
      type: 'task_management-task_commented';
    } & TaskCommentedNotification)
  | ({
      type: 'task_management-task_reporter_changed';
    } & TaskReporterChangedNotification)
  | ({
      type: 'custom_fields-expiration_notice';
    } & CustomFieldExpirationNotification)
  | ({
      type: 'advertisement-publishing_failed';
    } & ToastNotificationAdvertisementPublishingFailedNotice)
  | ({
      type: 'sourcing-filter_preset_update';
    } & ToastNotificationFilterPresetUpdateNotice)
  | ({
      type: 'vehicle-inspection_cloned';
    } & VehicleInspectionClonedNotification)
  | ({
      type: 'business_case-unread_message';
    } & ToastNotificationBusinessCaseHasUnreadMessage)
  | ({
      type: 'business_case-quick_unread_message';
    } & QuickToastNotificationBusinessCaseHasUnreadMessage)
  | ({
      type: 'customer_message-new_message';
    } & ToastNotificationCustomerMessageNewMessage)
  | ({
      type: 'customer_message-unread_message';
    } & ToastNotificationCustomerMessageUnreadMessage)
  | ({
      type: 'import-finished';
    } & ImportFinishedNotification)
  | ({
      type: 'vehicle-inspection_to_review';
    } & VehicleInspectionToReviewNotification)
  | ({
      type: 'vehicle-inspection_returned_for_completion';
    } & VehicleInspectionReturnedToCompletionNotification)
  | ({
      type: 'vehicle-inspection_finished';
    } & VehicleInspectionFinishedNotification)
  | ({
      type: 'integrated_service-change_announced';
    } & IntegratedServiceChangeNotification)
  | ({
      type: 'integrated_service-external_planner_conflict';
    } & IntegratedServiceExternalPlannerConflictNotification)
  | ({
      type: 'prompt_bridge-status_changed';
    } & PromptBridgeStatusChangedNotification)
  | ({
      type: 'offer-validity_expiration';
    } & ToastNotificationOfferValidityExpiration)
  | ({
      type: 'magic_publishing-finished';
    } & MagicPublishingFinishedNotification)
  | ({
      type: 'sale_vehicle_photo_checker-report_failed';
    } & ToastNotificationSaleVehiclePhotoCheckerReportFailed)
  | ({
      type: 'sale_vehicle_photo_checker-report_low_score';
    } & ToastNotificationSaleVehiclePhotoCheckerReportLowScore)
  | ({
      type: 'sale_vehicle_photo_checker-report_processing';
    } & BackgroundNotificationSaleVehiclePhotoCheckerReportProcessing)
  | ({
      type: 'car_pass-ready';
    } & CarPassReadyNotification)
  | ({
      type: 'car_pass-expired';
    } & CarPassExpiredNotification)
  | ({
      type: 'car_pass-failed';
    } & CarPassFailedNotification)
  | ({
      type: 'car_pass-action_required';
    } & CarPassActionRequiredNotification)
  | ({
      type: 'interest-vehicle_match';
    } & InterestVehicleMatchNotification)
  | ({
      type: 'rental_vehicle-mileage_limit_notice';
    } & RentalVehicleMileageLimitNotice)
  | ({
      type: 'rental_vehicle-availability_expiration_notice';
    } & RentalVehicleAvailabilityExpirationNotice)
  | ({
      type: 'accounting_subject-changed';
    } & BackgroundNotificationAccountingSubjectChanged)
  | ({
      type: 'business_case-online_offer_engagement_changed';
    } & BackgroundNotificationOnlineOfferEngagementChanged)
  | ({
      type: 'email_credentials-smtp_test_state_changed';
    } & BackgroundNotificationEmailCredentialsSmtpTestStateChanged)
  | ({
      type: 'custom_catalogue-remap_finished';
    } & CustomCatalogueRemapFinished)
  | ({
      type: 'user_absence-delegate_selected';
    } & UserAbsenceDelegateSelectedNotification)
  | ({
      type: 'user_absence-delegation_started';
    } & UserAbsenceDelegationStartedNotification)
  | ({
      type: 'user_absence-delegation_ended';
    } & UserAbsenceDelegationEndedNotification)
  | ({
      type: 'business_case-selling_invoice_paid';
    } & ToastNotificationBusinessCaseInvoicePaid)
  | ({
      type: 'business_case-buying_invoice_paid';
    } & ToastNotificationBusinessCaseInvoicePaid)
  | ({
      type: 'business_case-customer_rating_received';
    } & ToastNotificationBusinessCaseCustomerRatingReceived)
  | ({
      type: 'telephony-outbound_call_state_changed';
    } & TelephonyOutboundCallStateChangedNotification)
  | ({
      type: 'telephony-inbound_call_state_changed';
    } & TelephonyInboundCallStateChangedNotification)
  | ({
      type: 'communication-unread_changed';
    } & CommunicationUnreadChanged)
  | ({
      type: 'sales-financing_ending_sending_finished';
    } & ToastNotificationFinancingEndingSendingFinished)
  | ({
      type: 'sales-financing_ending_reminder';
    } & ToastNotificationFinancingEndingReminder);

export type ToastNotification = {
  id: string;
  type: string;
  createdAt: string;
  isRead: boolean;
  /**
   * The original intended recipient when the notification was delivered to their delegate during
   * an absence. Only the original recipient is carried, never the whole delegation chain.
   */
  delegatedFor?: User | null;
};

export type ImportFinishedNotification = ToastNotification & {
  payload: {
    importId: string;
    fileType: 'carisSaleVehicleCosts' | 'as3SaleVehicleCosts';
    importStatus: 'created' | 'scheduled' | 'inProgress' | 'finished' | 'failed';
    author: User;
    startedAt: string;
    finishedAt: string;
    affectedRecordsCount: number;
    errorsCount: number;
  };
};

export type Record = {
  id: string;
  resourceId:
    | 'CUSTOMER'
    | 'BUSINESS_CASE'
    | 'INTEREST'
    | 'SERVICE_CASE'
    | 'VEHICLE'
    | 'SERVICE_ORDER'
    | 'TASK'
    | 'SOURCING_VEHICLE_OFFER';
  name: string;
};

export type User = {
  id: string;
  name: string;
};

export type ParticipationAssigneeAssigned = ToastNotification & {
  payload: {
    record: Record;
    actor: User;
    assignedAt: string;
  };
};
export type ParticipationAssigneeUnassigned = ToastNotification & {
  payload: {
    record: Record;
    actor: User;
    unassignedAt: string;
  };
};
export type PermissionChanged = ToastNotification;
// Background doorbell without a payload: the read state of a communication has changed.
export type CommunicationUnreadChanged = ToastNotification;
export type DataGridExport = ToastNotification & {
  payload: {
    createdAt: string;
    exportId: string;
    dataGridCode: string;
    exportedAt: string;
    fileType: string;
  };
};
export type PresetChanged = ToastNotification & {
  payload: {
    createdAt: string;
    datagridCode: string;
    datagridPresetTitle: string;
    datagridTranslatedCode: string;
    sharedByUser: {id: string; name: string};
    sharedWithUser: {id: string; name: string};
  };
};

export type SaleVehiclePhotoRemoveBackgroundFinished = ToastNotification & {
  payload: {
    saleVehicle: {
      id: string;
      vehicleId: string;
      vehicleName: string;
    };
  };
};

export type ParticipationOwnerChanged = ToastNotification & {
  payload: {
    record: Record;
    actor: User;
    previousOwner: User | null;
    newOwner: User | null;
    changedAt: string;
  };
};

export type ToastNotificationParticipationOwnerAssigned = ToastNotification & {
  payload: {
    record: Record;
    actor: User;
    previousOwner: User | null;
    assignedAt: string;
  };
};

export type ToastNotificationParticipationOwnerUnassigned = ToastNotification & {
  payload: {
    record: Record;
    actor: User;
    newOwner: User | null;
    unassignedAt: string;
  };
};
export type Vehicle = {
  id: string;
  name: string;
};
export type VehicleInspectionExpirationNotice = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    vin?: string;
    registrationPlate?: string;
    daysUntilExpiration: number;
    validUntil: string;
  };
};
/**
 * An automation rule's `notify_internal` action (orchestration contract 1.0.28). One payload for
 * all three types: the type says whom the rule notified — the rule owner, the record's owner or
 * assignees, or a team — and the payload names the rule and the run.
 */
export type ToastNotificationAutomationRuleTriggered = ToastNotification & {
  payload: {
    ruleId: string;
    ruleName: string;
    runId: string;
    revision: number;
    actionIndex: number;
    record?: ToastNotificationAutomationRecord | null;
  };
};
/** To the rule owner: an action of the run waits until someone sends or skips its message. */
export type ToastNotificationAutomationRunAwaitingConfirmation = ToastNotification & {
  payload: {
    ruleId: string;
    ruleName: string;
    runId: string;
    actionIndex: number;
    record?: ToastNotificationAutomationRecord | null;
  };
};
/** To the rule owner: the run stopped or failed; `reason` is a run reason code. */
export type ToastNotificationAutomationRunStopped = ToastNotification & {
  payload: {
    ruleId: string;
    ruleName: string;
    runId: string;
    actionIndex: number;
    reason: string;
    record?: ToastNotificationAutomationRecord | null;
  };
};
/**
 * The record an automation run acted on, with the label it had when the notification was sent.
 * Null or absent when the run has no record, the recipient may not read it, the toast stands for
 * a scan batch, or it was sent before toasts named records.
 */
export type ToastNotificationAutomationRecord = {
  type: 'vehicle' | 'interest' | 'business_case' | 'invoice' | 'rental' | 'task' | 'complaint';
  id: string;
  label: string;
};
/** To the users who may approve the rule, not the requester: `revision` waits for approval. */
export type ToastNotificationAutomationRuleApprovalRequested = ToastNotification & {
  payload: {
    ruleId: string;
    ruleName: string;
    revision: number;
    requestedBy: User;
  };
};
export type VehicleNextServiceNotice = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    vin?: string;
    registrationPlate?: string;
    daysUntilNextService: number;
    validUntil: string;
  };
};
/** Nullable where it does not apply: the model for a make run, the targets for a model run. */
export type CustomCatalogueRemapFinished = ToastNotification & {
  payload: {
    result: 'succeeded' | 'alreadyRemapped';
    vehicleType: string;
    make: string;
    modelFamily: string | null;
    targetMake: string | null;
    targetModelFamily: string | null;
    updatedVehicleCount: number | null;
  };
};

export type RentalVehicleMileageLimitNotice = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    vin?: string | null;
    registrationPlate?: string | null;
    mileageLimit: number;
    currentMileage: number;
    kmUntilLimit: number;
    kmThreshold: number;
  };
};
export type RentalVehicleAvailabilityExpirationNotice = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    vin?: string | null;
    registrationPlate?: string | null;
    availableUntil: string;
    daysUntilUnavailable: number;
  };
};
export type ToastNotificationBusinessCaseHasUnreadMessagePayload = {
  customerId: string;
  customerName: string;
  record: Record;
};
export type ToastNotificationBusinessCaseHasUnreadMessage = ToastNotification & {
  payload: ToastNotificationBusinessCaseHasUnreadMessagePayload;
};
export type QuickToastNotificationBusinessCaseHasUnreadMessagePayload = {
  customerId: string;
  customerName: string;
  record: Record;
};
export type QuickToastNotificationBusinessCaseHasUnreadMessage = ToastNotification & {
  payload: QuickToastNotificationBusinessCaseHasUnreadMessagePayload;
};
export type ToastNotificationCustomerMessageNewMessagePayload = {
  customerId: string;
  customerName: string;
  record: Record | null;
};
export type ToastNotificationCustomerMessageNewMessage = ToastNotification & {
  payload: ToastNotificationCustomerMessageNewMessagePayload;
};
export type ToastNotificationCustomerMessageUnreadMessagePayload = {
  customerId: string;
  customerName: string;
  record: Record | null;
};
export type ToastNotificationCustomerMessageUnreadMessage = ToastNotification & {
  payload: ToastNotificationCustomerMessageUnreadMessagePayload;
};
/**
 * One transition of an outbound click-to-call, addressed to the user who started it. The payload
 * is the whole call state, so no follow-up fetch is needed; `ended` is terminal.
 */
export type TelephonyOutboundCallStateChangedNotification = ToastNotification & {
  payload: {
    callId: string;
    direction: 'outbound';
    status:
      | 'ringing_user'
      | 'waiting_for_confirmation'
      | 'connecting_customer'
      | 'in_call'
      | 'ended';
    outcome: 'connected' | 'customer_not_reached' | 'not_confirmed' | 'user_not_reached' | null;
    customerId: string;
    customerName: string | null;
    customerPhoneNumber: string;
    line: {id: string; name: string; phoneNumber: string};
    startedAt: string;
    answeredAt: string | null;
    connectedAt: string | null;
    endedAt: string | null;
    durationSeconds: number | null;
    answeredBy: null;
    /**
     * Absent in notifications sent before call recording existed. A copy of
     * `TelephonyRecordingNoticeModeSchema` in `@dms/api/settings`; keep the two in step.
     */
    recordingNoticeMode?: 'automatic' | 'operator' | null;
  };
};
/**
 * One transition of an inbound call, addressed to the users rung in the current step and, once
 * answered, to that step's users. The payload is the call as the recipient sees it.
 */
export type TelephonyInboundCallStateChangedNotification = ToastNotification & {
  payload: {
    callId: string;
    direction: 'inbound';
    status: 'ringing' | 'in_call' | 'ended';
    outcome: 'answered' | 'nobody_answered' | 'caller_hung_up' | 'passed_on' | null;
    customerId: string | null;
    customerName: string | null;
    customerPhoneNumber: string | null;
    line: {id: string; name: string; phoneNumber: string};
    startedAt: string;
    answeredAt: string | null;
    connectedAt: string | null;
    endedAt: string | null;
    durationSeconds: number | null;
    answeredBy: {id: string; name: string} | null;
    /**
     * Absent in notifications sent before call recording existed. A copy of
     * `TelephonyRecordingNoticeModeSchema` in `@dms/api/settings`; keep the two in step.
     */
    recordingNoticeMode?: 'automatic' | 'operator' | null;
  };
};
export type BatchDownloadBulkEventStateChanged = ToastNotification & {
  payload: {
    eventId: string;
    state: 'FAILED' | 'DONE';
    bulkObjectId: string | null;
    bulkObjectDownloadUrl: string | null;
  };
};
export type ToastNotificationBulkOperationStateChanged = ToastNotification & {
  payload: Pick<
    BulkOperationResponseBody,
    | 'bulkOperationId'
    | 'resourceId'
    | 'actionCode'
    | 'totalCount'
    | 'doneCount'
    | 'failedCount'
    | 'skippedCount'
  > & {
    actionLabel: string | null;
    state: Exclude<BulkOperationState, 'PENDING' | 'IN_PROGRESS'>;
  };
};
export type BulkOperationProgressPayload = {
  bulkOperationId: string;
  resourceId: string;
  actionCode: string;
  actionLabel: string | null;
  state: BulkOperationState;
  totalCount: number;
  doneCount: number;
  failedCount: number;
  skippedCount: number;
};
export type BackgroundNotificationBulkOperationProgressChanged = ToastNotification & {
  payload: BulkOperationProgressPayload;
};
export type ToastNotificationBulkOperationRecordsChanged = ToastNotification & {
  payload: BulkOperationProgressPayload;
};
export type VehicleFileOperationFinished = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    operation?: 'REMOVE_BACKGROUND' | 'ROTATE';
  };
};
export type VehiclePhotoCopyFinished = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    sourceAlbumId: string;
    destinationAlbumId: string;
  };
};
export type CommentMention = ToastNotification & {
  payload: {
    record: Record;
    author: User;
    commentId: string;
    commentHtml: string;
  };
};
export type CommentRecordOwner = CommentMention;

export type NewVehicleOrderRecord = Omit<Record, 'resourceId'> & {
  resourceId: 'NEW_VEHICLE_ORDER';
};

export type ToastNotificationNewVehicleFactoryConfirmation = ToastNotification & {
  payload: {
    record: NewVehicleOrderRecord;
    orderTitle: string;
    vehicle: Vehicle;
    kind: 'delivered' | 'cancelled';
  };
};

export type ToastNotificationNewVehicleFactoryVinConflict = ToastNotification & {
  payload: {
    record: NewVehicleOrderRecord;
    orderTitle: string;
    vehicle: Vehicle;
    vin: string;
  };
};

export type SaleVehicleReservationCancelled = ToastNotification & {
  payload: {
    vehicle: Vehicle;
  };
};

export type FileStorageFileUploadedNotification = ToastNotification & {
  payload: {
    fileId: string;
    fileStatus: string;
    fileType: string;
  };
};

export type FileStorageFileVariantUploadedNotification = ToastNotification & {
  payload: {
    originalFileId: string;
    variantFileId: string;
    fileStatus: string;
    fileType: string;
  };
};

export type VehicleDaysSinceLastRepriceNotification = ToastNotification & {
  payload: {
    daysSinceLastReprice: number;
    vehicle: Vehicle;
    vin: string | null;
  };
};

export type DigitalDocumentSignedNotification = ToastNotification & {
  payload: {
    record: Record | null;
    createdAt: string;
    signedBy: {
      id: string;
      name: string;
    };
    documentName: string;
    transactionType: string;
  };
};

export type VehicleWarrantyExpirationNotification = ToastNotification & {
  payload: {
    daysUntilExpiration: number;
    vehicle: Vehicle;
    vin: string | null;
    expiresAt: string;
  };
};

type TaskPayload = {
  task: {
    id: string;
    title: string;
  };
  user: {
    id: string;
    name: string;
  };
};

export type ToastNotificationVehicleUnregisteredNoticePayload = {
  vehicle: Vehicle;
  vin: string | null;
  daysUntilExpiration: number;
  registrationDueDate: string;
};

export type ToastNotificationVehicleUnregisteredNotice = ToastNotification & {
  payload: ToastNotificationVehicleUnregisteredNoticePayload;
};

export type TaskCreatedNotification = ToastNotification & {
  payload: TaskPayload;
};

export type TaskUpdatedNotification = ToastNotification & {
  payload: TaskPayload;
};

export type TaskDueNoticeNotification = ToastNotification & {
  payload: Pick<TaskPayload, 'task'> & {
    daysUntilDueDate: number;
    dueDate: string;
    dueTo: string;
  };
};

export type TaskCommentedNotification = ToastNotification & {
  payload: TaskPayload & {
    commentHtml: string;
  };
};

export type TaskReporterChangedNotification = ToastNotification & {
  payload: TaskPayload;
};

export type CustomFieldExpirationNotification = ToastNotification & {
  payload: {
    record: {
      id: string;
      resourceId: string;
      name: string;
      parent: unknown;
    };
    customFieldName: string;
    daysUntilExpiration: number;
    expiresAt: string;
  };
};

export type ToastNotificationAdvertisementPublishingFailedNotice = ToastNotification & {
  payload: {
    vehicle: Vehicle;
  };
};

export type ToastNotificationFilterPresetUpdateNotice = ToastNotification & {
  payload: {
    filterPresetId: string;
    filterPresetName: string;
    lastNotificationDate: string | null;
    currentNotificationDate: string;
    newVehiclesCount: number;
  };
};

export type Inspection = {
  id: string;
  name: string | null;
};

export type VehicleInspectionClonedNotification = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    inspection: Inspection;
  };
};

export type VehicleInspectionToReviewNotification = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    inspection: Inspection;
    vin: string | null;
  };
};

export type VehicleInspectionReturnedToCompletionNotification = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    inspection: Inspection;
    vin: string | null;
  };
};

export type VehicleInspectionFinishedNotification = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    inspection: Inspection;
    vin: string | null;
  };
};

export type IntegratedServiceChangeNotification = ToastNotification & {
  payload: {
    serviceCaseId: string;
    integratedServiceCode: 'MERCEDES_XENTRY_JOB' | 'DAIMLER_XENTRY_JOB';
    contextType: string;
    contextId: string;
  };
};

export type IntegratedServiceExternalPlannerConflictNotification = ToastNotification & {
  payload: {
    externalEventId: string;
    eventDateTime: string;
    contactInformation: string;
    recipients: string[];
  };
};

export type PromptBridgeStatusChangedNotification = ToastNotification & {
  payload: {
    messageId: string;
    status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED';
  };
};

export type ToastNotificationOfferValidityExpirationPayload = {
  businessCase: {
    id: string;
    number: string;
  };
  customer: {
    id: string;
    fullName: string;
  };
};

export type ToastNotificationOfferValidityExpiration = ToastNotification & {
  payload: ToastNotificationOfferValidityExpirationPayload;
};

export type MagicPublishingFinishedNotification = ToastNotification & {
  payload: {
    saleVehicle: {
      id: string;
      vehicleId: string;
      vehicleName: string;
    };
    urls: string[];
  };
};

export type BackgroundNotificationSaleVehiclePhotoCheckerReportProcessingPayload = {
  saleVehicleId: string;
  saleVehicleAlbumId: string;
  photoCheckerReportId: string;
  status: string;
  photosUpdatedAt?: string | null;
  photocheckerRunAt: string;
};

export type BackgroundNotificationSaleVehiclePhotoCheckerReportProcessing = ToastNotification & {
  payload: BackgroundNotificationSaleVehiclePhotoCheckerReportProcessingPayload;
};

export type AccountingSubjectContextEntry = {
  type: LiteralUnionAutocomplete<ValueOf<typeof ACCOUNTING_SUBJECT_VOCABULARY.contextTypes>>;
  id: string;
};

/**
 * Background-only doorbell fired when the external accounting changes a subject
 * (today a checkout order): an issuance milestone, a paid payment, a bound
 * document, an attached printout. It says WHICH subject moved, not what it looks
 * like now. `sourceType`, context `type` and `reason` are open vocabularies owned
 * by the backend, hence the string fallbacks.
 */
export type BackgroundNotificationAccountingSubjectChangedPayload = {
  subject: {
    sourceType: LiteralUnionAutocomplete<ValueOf<typeof ACCOUNTING_SUBJECT_VOCABULARY.sourceTypes>>;
    sourceId: string;
  };
  context: AccountingSubjectContextEntry[];
  reason: LiteralUnionAutocomplete<ValueOf<typeof ACCOUNTING_SUBJECT_VOCABULARY.reasons>>;
  documentId: string | null;
};

export type BackgroundNotificationAccountingSubjectChanged = ToastNotification & {
  payload: BackgroundNotificationAccountingSubjectChangedPayload;
};

/**
 * Background-only doorbell fired when the customer opens or updates an online offer
 * of a business case. It says WHICH business case moved, not what it looks like now.
 */
export type BackgroundNotificationOnlineOfferEngagementChangedPayload = {
  businessCaseId: string;
  offerId: string;
  reason: 'opened' | 'updated';
};

export type BackgroundNotificationOnlineOfferEngagementChanged = ToastNotification & {
  payload: BackgroundNotificationOnlineOfferEngagementChangedPayload;
};

/**
 * Background-only full snapshot of an SMTP credentials test run.
 * `overallStatus`, `tests[].type` and `tests[].status` are deliberately plain
 * strings: the backend owns their vocabulary and may extend it, so unknown
 * values must never throw.
 * Current test types: connection | tls | authentication.
 * Test statuses: todo | in_progress | warning | failed | done | skip.
 * Overall statuses: in_progress | warning | failed | done.
 * Reasons (set on failed and warning checks, null otherwise): host_not_allowed | host_unresolvable | connection_timed_out | connection_failed | start_tls_failed | tls_unavailable | authentication_failed | unexpected_error.
 */
export type BackgroundNotificationEmailCredentialsSmtpTestStateChangedPayload = {
  testRunId: string;
  overallStatus: string;
  tests: {
    type: string;
    status: string;
    message: string | null;
    reason: string | null;
  }[];
};

export type BackgroundNotificationEmailCredentialsSmtpTestStateChanged = ToastNotification & {
  payload: BackgroundNotificationEmailCredentialsSmtpTestStateChangedPayload;
};

export type ToastNotificationSaleVehiclePhotoCheckerReportFailedPayload = {
  saleVehicle: {
    id: string;
    vehicleId: string;
    vehicleName: string;
  };
  saleVehicleAlbumId: string;
  photoCheckerReportId: string;
  message: string;
};

export type ToastNotificationSaleVehiclePhotoCheckerReportFailed = ToastNotification & {
  payload: ToastNotificationSaleVehiclePhotoCheckerReportFailedPayload;
};

export type ToastNotificationSaleVehiclePhotoCheckerReportLowScorePayload = {
  saleVehicle: {
    id: string;
    vehicleId: string;
    vehicleName: string;
  };
  saleVehicleAlbumId: string;
  photoCheckerReportId: string;
  overallScore: number;
};

export type ToastNotificationSaleVehiclePhotoCheckerReportLowScore = ToastNotification & {
  payload: ToastNotificationSaleVehiclePhotoCheckerReportLowScorePayload;
};

export type CarPassReadyNotification = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    url: string;
  };
};

export type CarPassExpiredNotification = ToastNotification & {
  payload: {
    vehicle: Vehicle;
  };
};

export type CarPassFailedNotification = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    reason: string;
  };
};

export type CarPassActionRequiredNotification = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    issues: string[];
  };
};

export type InterestVehicleMatchNotification = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    interests: {id: string; name: string}[];
  };
};

type UserAbsenceDelegationPayload = {
  absentUser: User;
  dateFrom: string;
  dateTo: string | null;
};

export type UserAbsenceDelegateSelectedNotification = ToastNotification & {
  payload: UserAbsenceDelegationPayload;
};

export type UserAbsenceDelegationStartedNotification = ToastNotification & {
  payload: UserAbsenceDelegationPayload;
};

export type UserAbsenceDelegationEndedNotification = ToastNotification & {
  payload: {
    absentUser: User;
  };
};

export type ToastNotificationBusinessCaseInvoicePaidPayload = {
  businessCase: {
    id: string;
    number: string;
  };
  vehicle: Vehicle | null;
  vin: string | null;
  registrationPlate: string | null;
  documentType: 'ADVANCE_INVOICE' | 'FINAL_INVOICE';
  documentNumber: string;
  isFullyPaid: boolean;
};

export type ToastNotificationBusinessCaseInvoicePaid = ToastNotification & {
  payload: ToastNotificationBusinessCaseInvoicePaidPayload;
};

export type ToastNotificationFinancingEndingSendingFinishedPayload = {
  bulkOperationId: string;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  uncertainCount: number | null;
  rerunOfBulkOperationId?: string | null;
};

export type ToastNotificationFinancingEndingSendingFinished = ToastNotification & {
  payload: ToastNotificationFinancingEndingSendingFinishedPayload;
};

export type FinancingEndingReminderLeadTimeDays = '0' | '1' | '7' | '14' | '30' | '60' | '90';

export type FinancingEndingReminderVehicle = {
  businessCaseId: string;
  vehicleId: string;
  customerName: string | null;
  vehicleTitle: string | null;
  endOfFinancing: string;
  // Sent by newer backends only; null when unknown.
  registrationPlate?: string | null;
  vin?: string | null;
  businessCaseNumber?: string | null;
};

export type ToastNotificationFinancingEndingReminderPayload = {
  leadTimeDays: FinancingEndingReminderLeadTimeDays;
  targetEndDates: string[];
  vehicles: FinancingEndingReminderVehicle[];
};

export type ToastNotificationFinancingEndingReminder = ToastNotification & {
  payload: ToastNotificationFinancingEndingReminderPayload;
};

export type ToastNotificationBusinessCaseCustomerRatingReceivedPayload = {
  businessCaseId: string;
  businessCaseNumber: string;
  customerName: string;
  vehicleTitle: string | null;
  rating: number;
  recommendation: number | null;
  commentFirstLine: string | null;
  needsAttention: boolean;
};

export type ToastNotificationBusinessCaseCustomerRatingReceived = ToastNotification & {
  payload: ToastNotificationBusinessCaseCustomerRatingReceivedPayload;
};

export type ParameterResponseBody = {
  code: string;
  label: string;
  description: string;
  example: string;
};

export type ListContextParametersApiResponse = ParameterResponseBody[];
export type TemplateContextEnum =
  | 'customer'
  | 'service-order'
  | 'business-case'
  | 'rental-order'
  | 'vehicle'
  | 'tire-set'
  | 'interest'
  | 'workshop-interest';
export type ListContextParametersApiArg = {
  context: TemplateContextEnum;
};

export type CreateMessageTemplateRequestBody = {
  locale: string;
  name: string;
  context: TemplateContextEnum;
  body: string;
  subject?: string | null;
};
export type MessageTemplateResponseBody = {
  name: string;
  context: TemplateContextEnum;
  body: string;
  subject?: string | null;
};

export type CreateMessageTemplateApiResponse = UuidResponseBodyMessaging;
export type CreateMessageTemplateApiArg = {
  templateType: TemplateTypeEnum;
  createTemplateRequestBody: CreateMessageTemplateRequestBody;
};

export type GetMessageTemplateApiResponse = MessageTemplateResponseBody;
export type GetMessageTemplateApiArg = {
  templateType: TemplateTypeEnum;
  templateId: string;
};

export type PatchMessageTemplateRequestBody = {
  name?: string | null;
  body?: string | null;
  subject?: string | null;
};

export type PatchMessageTemplateApiResponse = UuidResponseBodyMessaging;
export type PatchMessageTemplateApiArg = {
  templateType: TemplateTypeEnum;
  templateId: string;
  patchTemplateRequestBody: PatchMessageTemplateRequestBody;
};

export type DeleteMessageTemplateApiResponse = unknown;
export type DeleteMessageTemplateApiArg = {
  templateType: TemplateTypeEnum;
  templateId: string;
};
export type UuidResponseBodyMessaging = {
  id: string;
};
export type ContextTemplateResponseBody = {
  id: string;
  name: string;
  context: TemplateContextEnum;
  type: TemplateTypeEnum;
  body: string;
  subject?: string | null;
  locale?: Locale | null;
};

export type GetContextTemplateListApiResponse = ContextTemplateResponseBody[];
export type GetContextTemplateListApiArg = {
  type?: TemplateTypeEnum;
  context?: TemplateContextEnum;
};

export type TemplateTypeEnum = 'email' | 'sms' | 'chat';

export type EmailTemplateResponseBody = {
  name: string;
  type: TemplateTypeEnum;
  subject: string;
  body: string;
};
export type CustomerContextTemplateRequestBody = {
  customerId: string;
};
export type SmsTemplateResponseBody = {
  name: string;
  type: TemplateTypeEnum;
  body: string;
};

export const renderEmailTemplateResponseBodySchema = z.object({
  subject: z.string(),
  body: z.string(),
});
export type RenderEmailTemplateResponseBody = z.infer<typeof renderEmailTemplateResponseBodySchema>;
export const renderEmailTemplateRequestBodySchema = z.object({
  subject: z.string(),
  body: z.string(),
});
export type RenderEmailTemplateRequestBody = z.infer<typeof renderEmailTemplateRequestBodySchema>;
export type RenderCustomerEmailTemplateRequestBody = {
  template: RenderEmailTemplateRequestBody;
  customerId: string;
};
export const renderSmsTemplateResponseBodySchema = z.object({
  body: z.string(),
});
export type RenderSmsTemplateResponseBody = z.infer<typeof renderSmsTemplateResponseBodySchema>;
export type RenderCustomerSmsTemplateRequestBody = {
  template: string;
  customerId: string;
};
export const renderSmsTemplateRequestBodySchema = z.object({
  template: z.string(),
});
export type RenderSmsTemplateRequestBody = z.infer<typeof renderSmsTemplateRequestBodySchema>;
export const localeSchema = z.enum([
  'cs-CZ',
  'de-DE',
  'de-BE',
  'en-GB',
  'en-US',
  'es-ES',
  'fr-FR',
  'fr-BE',
  'it-IT',
  'pl-PL',
  'sk-SK',
  'nl-BE',
  'nl-NL',
]);

export type RenderCustomerEmailTemplateApiResponse = RenderEmailTemplateResponseBody;
export type RenderCustomerEmailTemplateApiArg = {
  renderCustomerEmailTemplateRequestBody: RenderCustomerEmailTemplateRequestBody;
};
export type RenderCustomerEmailTemplateExampleApiResponse = RenderEmailTemplateResponseBody;
export type RenderCustomerEmailTemplateExampleApiArg = {
  body: RenderEmailTemplateRequestBody;
};
export type RenderCustomerSmsTemplateApiResponse = RenderSmsTemplateResponseBody;
export type RenderCustomerSmsTemplateApiArg = {
  renderCustomerSmsTemplateRequestBody: RenderCustomerSmsTemplateRequestBody;
};
export type RenderCustomerSmsTemplateExampleApiResponse = RenderSmsTemplateResponseBody;
export type RenderCustomerSmsTemplateExampleApiArg = {
  body: RenderSmsTemplateRequestBody;
};

export type RenderBusinessCaseEmailTemplateRequestBody = {
  template: RenderEmailTemplateRequestBody;
  businessCaseId: string;
};

export type RenderBusinessCaseEmailTemplateApiResponse = RenderEmailTemplateResponseBody;
export type RenderBusinessCaseEmailTemplateApiArg = {
  renderBusinessCaseEmailTemplateRequestBody: RenderBusinessCaseEmailTemplateRequestBody;
};

export type RenderBusinessCaseSmsTemplateRequestBody = {
  template: string;
  businessCaseId: string;
};

export type RenderBusinessCaseSmsTemplateApiResponse = RenderSmsTemplateResponseBody;
export type RenderBusinessCaseSmsTemplateApiArg = {
  renderBusinessCaseSmsTemplateRequestBody: RenderBusinessCaseSmsTemplateRequestBody;
};

export type RenderServiceOrderEmailTemplateRequestBody = {
  template: RenderEmailTemplateRequestBody;
  serviceOrderId: string;
  serviceCaseId: string;
};
export type RenderServiceOrderSmsTemplateRequestBody = {
  template: string;
  serviceOrderId: string;
  serviceCaseId: string;
};
export type RenderServiceOrderEmailTemplateApiResponse = RenderEmailTemplateResponseBody;
export type RenderServiceOrderEmailTemplateApiArg = {
  renderServiceOrderEmailTemplateRequestBody: RenderServiceOrderEmailTemplateRequestBody;
};
export type RenderServiceOrderEmailTemplateExampleApiResponse = RenderEmailTemplateResponseBody;
export type RenderServiceOrderEmailTemplateExampleApiArg = {
  body: RenderEmailTemplateRequestBody;
};
export type RenderServiceOrderSmsTemplateApiResponse = RenderSmsTemplateResponseBody;
export type RenderServiceOrderSmsTemplateApiArg = {
  renderServiceOrderSmsTemplateRequestBody: RenderServiceOrderSmsTemplateRequestBody;
};
export type RenderServiceOrderSmsTemplateExampleApiResponse = RenderSmsTemplateResponseBody;
export type RenderServiceOrderSmsTemplateExampleApiArg = {
  body: RenderSmsTemplateRequestBody;
};
export type Obligation = 'system' | 'admin' | 'user';
export type Channel =
  | 'email'
  | 'sms'
  | 'toastNotification'
  | 'desktopNotification'
  | 'appNotification';
export type ReceiveMessagePreference =
  | 'always'
  | 'ownerOrAssignee'
  | 'owner'
  | 'assignee'
  | 'never';
export type MessagingPreferences = {
  email: ReceiveMessagePreference;
  sms: ReceiveMessagePreference;
  toastNotification: ReceiveMessagePreference;
  desktopNotification: ReceiveMessagePreference;
  appNotification: ReceiveMessagePreference;
};
export type NotificationDefinitionResponseBody = {
  id: string;
  name: string;
  description: string;
  obligation: Obligation;
  channels: Channel[];
  defaultRecipientSettings: MessagingPreferences;
  isEnabled: boolean;
};
export type ListNotificationDefinitionsApiResponse = NotificationDefinitionResponseBody[];
export type ListNotificationDefinitionsApiArg = void;

export type GetNotificationDefinitionsApiArg = {id: string};
export type GetNotificationDefinitionsApiResponse = NotificationDefinitionResponseBody;

export type GetToastNotificationExampleApiResponse = SentToastNotificationResponseBody;
export type GetToastNotificationExampleApiArg = {
  id: string;
};

export type PreviewEmailNotificationApiResponse = string;
export type PreviewEmailNotificationApiArg = {
  id: string;
  locale: string;
};

export type Receive = 'always' | 'never';

export type NotificationReceiveSettingsResponseBody = {
  email: Receive | null;
  sms: Receive | null;
  toastNotification: Receive | null;
  desktopNotification: Receive | null;
  appNotification: Receive | null;
};
export type NotificationSettingsResponseBody = {
  systemReceiveSettings: NotificationReceiveSettingsResponseBody;
  /** Tenant may specify his own settings if the notification allows it, overriding system settings and applying as default to user settings. These settings might be overriden by users in case they are allowed to have their own settings and in case tenant settings are not forced. */
  tenantReceiveSettings: NotificationReceiveSettingsResponseBody | null;
  /** User may specify his own settings if the notification allows it, overriding system and tenant settings. These settings might be overriden by tenant forcing his settings. */
  userReceiveSettings: NotificationReceiveSettingsResponseBody | null;
  /** There will always be default system settings, if the notificaiton allows it. */
  systemSettings: any | null; // eslint-disable-line typescript/no-explicit-any -- Pre-existing any
  /** There may be settings specified by tenant, if the notificaiton allows it. */
  tenantSettings: any | null; // eslint-disable-line typescript/no-explicit-any -- Pre-existing any
  /** True if tenant settings are forced above user settings, false if not and null in case there is no tenant setting or there even can't be. */
  tenantSettingsForced: boolean | null;
};

export type PatchTenantNotificationSettingsApiResponse = /** status 204  */ void;
export type PatchTenantNotificationSettingsApiArg = {
  id: string;
  patchTenantNotificationSettingsRequestBody: PatchTenantNotificationSettingsRequestBody;
};
export type PatchUserNotificationSettingsApiResponse = /** status 204  */ void;
export type PatchUserNotificationSettingsApiArg = {
  id: string;
  patchUserNotificationSettingsRequestBody: PatchUserNotificationSettingsRequestBody;
};
export type PatchTenantNotificationSettingsRequestBody = {
  email?: Receive | null;
  sms?: Receive | null;
  toastNotification?: Receive | null;
  desktopNotification?: Receive | null;
  appNotification?: Receive | null;
  settings?: object | null;
  force?: boolean | null;
};
export type PatchUserNotificationSettingsRequestBody = {
  email?: Receive | null;
  sms?: Receive | null;
  toastNotification?: Receive | null;
  desktopNotification?: Receive | null;
  appNotification?: Receive | null;
};
export type GetNotificationSettingsApiResponse =
  /** status 200  */ NotificationSettingsResponseBody;
export type GetNotificationSettingsApiArg = {
  id: string;
};

export type CreateSignatureApiResponse = /** status 201  */ UuidResponseBody;
export type CreateSignatureApiArg = {
  /** User JWT tokens are allowed */
  authorization?: string;
  updateSignatureRequestBody: UpdateSignatureRequestBody;
};
export type ListSignaturesApiResponse = /** status 200  */ SignatureResponseBody[];
export type ListSignaturesApiArg = void;
export type RemoveSignatureApiResponse = /** status 204  */ void;
export type RemoveSignatureApiArg = {
  id: string;
};
export type UpdateSignatureApiResponse = /** status 204  */ void;
export type UpdateSignatureApiArg = {
  id: string;
  updateSignatureRequestBody: UpdateSignatureRequestBody;
};
export type SignatureContentType = 'string' | 'html';
export type UpdateSignatureRequestBody = {
  signature: string;
  /** Defaults to `'string'` (plain text) on the backend when omitted. */
  contentType?: SignatureContentType;
};
export type SignatureResponseBody = {
  id: string;
  signature: string;
  contentType: SignatureContentType;
};
export type GetSignatureImagePermanentUriApiResponse =
  /** status 200  */ SignatureImagePermanentUriResponseBody;
export type GetSignatureImagePermanentUriApiArg = {
  fileId: string;
};
export type SignatureImagePermanentUriResponseBody = {
  permanentUri: string;
};

export type EmailCredentialsOwnerType = 'user' | 'tenant';
export type CreateSmtpCredentialsRequestBody = {
  ownerType: EmailCredentialsOwnerType;
  title: string;
  host: string;
  port: number;
  username: string;
  password: string;
  emailAddress: string;
};

export type TestSmtpEmailCredentialsRequestBody = {
  host: string;
  port: number;
  username: string;
  password: string;
  emailAddress: string;
};

export type PatchOAuthEmailCredentialsApiResponse = /** status 204  */ void;
export type PatchOAuthEmailCredentialsApiArg = {
  id: string;
  patchOAuthEmailCredentialsRequestBody: PatchOAuthEmailCredentialsRequestBody;
};
export type PatchOAuthEmailCredentialsRequestBody = {
  title?: string | null;
  emailAddress?: string | null;
};

export type OAuthProviders = 'microsoft';

export type SmtpCredentialsResponseBody = {
  /** SMTP credentials identifier */
  id: string;
  /** Email credentials kind */
  type: 'smtp';
  ownerType: EmailCredentialsOwnerType;
  title: string;
  host: string;
  port: number;
  username: string;
  emailAddress: string;
  /** Whether these credentials are the default mailbox of the current user */
  isDefault: boolean;
  /** Creation timestamp */
  createdAt: string;
  /** Last update timestamp */
  updatedAt: string | null;
  /** Creator user ID */
  createdBy: string;
  /** Last updater user ID */
  updatedBy: string | null;
};
export type OAuthEmailCredentialsResponseBody = {
  id: string;
  type: 'oauth';
  ownerType: EmailCredentialsOwnerType;
  provider: OAuthProviders;
  title: string;
  emailAddress: string;
  /** Whether these credentials are the default mailbox of the current user */
  isDefault: boolean;
  /** Creation timestamp */
  createdAt: string;
  /** Last update timestamp */
  updatedAt: string | null;
  /** Creator user ID */
  createdBy: string;
  /** Last updater user ID */
  updatedBy: string | null;
};
export type ListEmailCredentialsResponseBody = {
  credentials: (SmtpCredentialsResponseBody | OAuthEmailCredentialsResponseBody)[];
};
export type SetDefaultEmailCredentialsRequestBody = {
  /** Email credentials to use as the default mailbox of the current user */
  emailCredentialsId: string;
};
export type PatchSmtpCredentialsRequestBody = {
  title?: string | null;
  host?: string | null;
  port?: number | null;
  username?: string | null;
  password: string;
  emailAddress?: string | null;
};
export type MicrosoftOAuth2Purpose = 'calendar' | 'email';
export type StartMicrosoftOAuth2RequestBody = {
  callbackUrl: string;
  purposes: MicrosoftOAuth2Purpose[];
};
export type MicrosoftOAuth2AuthUrlResponseBody = {
  authUrl: string;
};
export type CompleteMicrosoftOAuth2RequestBody = {
  code: string;
  state: string;
  callbackUrl: string;
};
export type MicrosoftOAuth2IdentityResponseBody = {
  displayName: string | null;
  email: string | null;
};
export type CompleteMicrosoftOAuth2ResponseBody = {
  microsoftTokenId: string;
  identity: MicrosoftOAuth2IdentityResponseBody;
  purposes: MicrosoftOAuth2Purpose[];
};
export type CreateMicrosoftOAuth2CredentialsRequestBody = {
  ownerType: EmailCredentialsOwnerType;
  oAuthTokenId: string;
  emailAddress: string;
  title: string;
};

export type DiscoverEmailProviderApiResponse = /** status 200  */ DiscoverEmailProviderResponseBody;
export type DiscoverEmailProviderApiArg = {
  discoverEmailProviderRequestBody: DiscoverEmailProviderRequestBody;
};
export type ListEmailProviderApiResponse = /** status 200  */ ListEmailProviderResponseBody;
export type ListEmailProviderApiArg = void;
export type CreateEmailSmtpCredentialsApiResponse = /** status 201  */ UuidResponseBody;
export type CreateEmailSmtpCredentialsApiArg = {
  createSmtpCredentialsRequestBody: CreateSmtpCredentialsRequestBody;
};
export type TestEmailSmtpCredentialsApiResponse = /** status 202  */ UuidResponseBody;
export type TestEmailSmtpCredentialsApiArg = {
  testSmtpEmailCredentialsRequestBody: TestSmtpEmailCredentialsRequestBody;
};
export type StartMicrosoftOAuth2ApiResponse = /** status 200  */ MicrosoftOAuth2AuthUrlResponseBody;
export type StartMicrosoftOAuth2ApiArg = {
  startMicrosoftOAuth2RequestBody: StartMicrosoftOAuth2RequestBody;
};
export type CompleteMicrosoftOAuth2ApiResponse =
  /** status 200  */ CompleteMicrosoftOAuth2ResponseBody;
export type CompleteMicrosoftOAuth2ApiArg = {
  completeMicrosoftOAuth2RequestBody: CompleteMicrosoftOAuth2RequestBody;
};
export type CreateMicrosoftOAuth2CredentialsApiResponse = /** status 201  */ UuidResponseBody;
export type CreateMicrosoftOAuth2CredentialsApiArg = {
  createMicrosoftOAuth2CredentialsRequestBody: CreateMicrosoftOAuth2CredentialsRequestBody;
};
export type DeleteEmailCredentialsApiResponse = /** status 204  */ void;
export type DeleteEmailCredentialsApiArg = {
  id: string;
};
export type ListEmailCredentialsApiResponse = /** status 200  */ ListEmailCredentialsResponseBody;
export type ListEmailCredentialsApiArg = {
  ownerType?: EmailCredentialsOwnerType;
};
export type SetDefaultEmailCredentialsApiResponse = /** status 204  */ void;
export type SetDefaultEmailCredentialsApiArg = {
  setDefaultEmailCredentialsRequestBody: SetDefaultEmailCredentialsRequestBody;
};
export type ClearDefaultEmailCredentialsApiResponse = /** status 204  */ void;
export type ClearDefaultEmailCredentialsApiArg = void;
export type PatchEmailSmtpCredentialsApiResponse = /** status 204  */ void;
export type PatchEmailSmtpCredentialsApiArg = {
  id: string;
  /** User JWT tokens are allowed */
  authorization?: string;
  patchSmtpCredentialsRequestBody: PatchSmtpCredentialsRequestBody;
};
export type SmtpCredentialsStubResponseBody = {
  host: string | null;
  port: number | null;
  username: string | null;
  password: string | null;
  emailAddress: string | null;
};
export type SmtpEmailProviderCode =
  | 'gmail-smtp'
  | 'outlook-smtp'
  | 'seznam-smtp'
  | 'custom-smtp'
  | 'microsoft-oauth';
export type SmtpEmailProviderResponseBody = {
  code: SmtpEmailProviderCode;
  type: 'smtp';
  credentials: SmtpCredentialsStubResponseBody;
};
export type MicrosoftOAuthEmailProviderResponseBody = {
  code: 'microsoft-oauth';
  type: 'oauth2';
  credentials: null;
};
export type EmailProviderResponseBody =
  | SmtpEmailProviderResponseBody
  | MicrosoftOAuthEmailProviderResponseBody;
export type DiscoverEmailProviderResponseBody = {
  providers: SmtpEmailProviderResponseBody[];
};
export type DiscoverEmailProviderRequestBody = {
  emailAddress: string;
  password?: string | null;
};
export type ListEmailProviderResponseBody = {
  providers: EmailProviderResponseBody[];
};
export type GetLastKnownEmailCredentialsApiResponse = {
  smtpCredentialsId: string;
};

export type CustomerMessageIdEnum =
  | 'customer-message'
  | 'service-customer_check_in'
  | 'service-customer_handover'
  | 'business_case-online_offer'
  | 'business_case-buying_online_offer'
  | 'business_case-combined_online_offer';
export type GetLastKnownEmailCredentialsApiArg = {
  customerMessageId: CustomerMessageIdEnum;
};
export type SendCustomerSmsApiResponse = /** status 204  */ void;
export type SendCustomerSmsApiArg = {
  sendCustomerSmsRequestBody: SendCustomerSmsRequestBody;
};
export type SendCustomerSmsRequestBody = {
  body: string;
  customerId: string;
  customerPhoneNumber: PhoneNumber;
  recordId?: string | null;
  resourceId?: string | null;
  secondaryRecordIds?: SecondaryRecordId[];
};

export type SendWhatsAppContentMessageApiResponse = /** status 204  */ void;
export type SendWhatsAppContentMessageApiArg = {
  sendWhatsAppContentMessageRequestBody: SendWhatsAppContentMessageRequestBody;
};
export type SendWhatsAppContentMessageRequestBody = {
  to: string;
  messageContent: string;
  customerId: string;
  recordId: string;
  resourceId: EntityResourceIds;
  chatConversationId?: string | null;
};

export type RenderCustomerMessageEmailTemplateApiResponse =
  /** status 200  */ RenderCustomerMessageEmailTemplateResponseBody;
export type RenderCustomerMessageEmailTemplateApiArg = {
  customerMessageId: string;
  renderCustomerMessageRequestBody: RenderCustomerMessageRequestBody;
};
export type RenderCustomerMessageEmailTemplateResponseBody = {
  subject: string;
  body: string;
};

export type RenderCustomerMessageRequestBody = {
  customerId: string;
  customerMessageId?:
    | (
        | 'business_case-online_offer'
        | 'customer-message'
        | 'service-customer_check_in'
        | 'service-customer_handover'
      )
    | null;
  payload:
    | BusinessCaseOnlineOfferPayloadRequestBody
    | ServiceCheckInPayloadRequestBody
    | ServiceHandoverPayloadRequestBody
    | GeneralCustomerMessagePayloadRequestBody;
  locale?: string | null;
  bodyFormat?: MessageContentFormat;
};

export type RenderCustomerMessageSmsTemplateApiResponse =
  /** status 200  */ RenderCustomerMessageTemplateResponseBody;
export type RenderCustomerMessageSmsTemplateApiArg = {
  customerMessageId: string;
  renderCustomerMessageRequestBody: RenderCustomerMessageRequestBody;
};
export type RenderCustomerMessageTemplateResponseBody = {
  body: string;
};
export type RenderVehicleEmailTemplateRequestBody = {
  template: RenderEmailTemplateRequestBody;
  vehicleId: string;
};
export type RenderVehicleSmsTemplateRequestBody = {
  template: string;
  vehicleId: string;
};
export type RenderRentalOrderSmsTemplateRequestBody = {
  template: string;
  rentalOrderId: string;
};
export type RenderRentalOrderEmailTemplateRequestBody = {
  template: RenderEmailTemplateRequestBody;
  rentalOrderId: string;
};
export type RenderRentalOrderEmailTemplateApiResponse =
  /** status 200  */ RenderEmailTemplateResponseBody;
export type RenderRentalOrderEmailTemplateApiArg = {
  body: RenderRentalOrderEmailTemplateRequestBody;
};
export type RenderRentalOrderEmailTemplateExampleApiResponse =
  /** status 200  */ RenderEmailTemplateResponseBody;
export type RenderRentalOrderEmailTemplateExampleApiArg = {
  body: RenderEmailTemplateRequestBody;
};
export type RenderRentalOrderSmsTemplateApiResponse =
  /** status 200  */ RenderSmsTemplateResponseBody;
export type RenderRentalOrderSmsTemplateApiArg = {
  body: RenderRentalOrderSmsTemplateRequestBody;
};
export type RenderRentalOrderSmsTemplateExampleApiResponse =
  /** status 200  */ RenderSmsTemplateResponseBody;
export type RenderRentalOrderSmsTemplateExampleApiArg = {
  body: RenderSmsTemplateRequestBody;
};
export type RenderVehicleEmailTemplateApiResponse =
  /** status 200  */ RenderEmailTemplateResponseBody;
export type RenderVehicleEmailTemplateApiArg = {
  body: RenderVehicleEmailTemplateRequestBody;
};
export type RenderVehicleEmailTemplateExampleApiResponse =
  /** status 200  */ RenderEmailTemplateResponseBody;
export type RenderVehicleEmailTemplateExampleApiArg = {
  body: RenderEmailTemplateRequestBody;
};
export type RenderVehicleSmsTemplateApiResponse = /** status 200  */ RenderSmsTemplateResponseBody;
export type RenderVehicleSmsTemplateApiArg = {
  body: RenderVehicleSmsTemplateRequestBody;
};
export type RenderVehicleSmsTemplateExampleApiResponse =
  /** status 200  */ RenderSmsTemplateResponseBody;
export type RenderVehicleSmsTemplateExampleApiArg = {
  body: RenderSmsTemplateRequestBody;
};
export const renderTireSetEmailTemplateExampleApiArgSchema = z.object({
  body: renderEmailTemplateRequestBodySchema,
});
export type RenderTireSetEmailTemplateExampleApiArg = z.infer<
  typeof renderTireSetEmailTemplateExampleApiArgSchema
>;
export type RenderTireSetEmailTemplateExampleApiResponse = RenderEmailTemplateResponseBody;
export const renderTireSetSmsTemplateExampleApiArgSchema = z.object({
  body: renderSmsTemplateRequestBodySchema,
});
export type RenderTireSetSmsTemplateExampleApiArg = z.infer<
  typeof renderTireSetSmsTemplateExampleApiArgSchema
>;
export type RenderTireSetSmsTemplateExampleApiResponse = RenderSmsTemplateResponseBody;

export const renderInterestEmailTemplateRequestBodySchema = z.object({
  subject: z.string(),
  body: z.string(),
  interestId: z.string(),
  locale: localeSchema,
});
export type RenderInterestEmailTemplateRequestBody = z.infer<
  typeof renderInterestEmailTemplateRequestBodySchema
>;
export const renderInterestEmailTemplateApiArgSchema = z.object({
  body: renderInterestEmailTemplateRequestBodySchema,
});
export type RenderInterestEmailTemplateApiArg = z.infer<
  typeof renderInterestEmailTemplateApiArgSchema
>;
export type RenderInterestEmailTemplateApiResponse = RenderEmailTemplateResponseBody;
export const renderInterestEmailTemplateExampleApiArgSchema = z.object({
  body: renderEmailTemplateRequestBodySchema,
});
export type RenderInterestEmailTemplateExampleApiArg = z.infer<
  typeof renderInterestEmailTemplateExampleApiArgSchema
>;
export type RenderInterestEmailTemplateExampleApiResponse = RenderEmailTemplateResponseBody;
export const renderInterestSmsTemplateRequestBodySchema = z.object({
  template: z.string(),
  interestId: z.string(),
  locale: localeSchema,
});
export type RenderInterestSmsTemplateRequestBody = z.infer<
  typeof renderInterestSmsTemplateRequestBodySchema
>;
export const renderInterestSmsTemplateApiArgSchema = z.object({
  body: renderInterestSmsTemplateRequestBodySchema,
});
export type RenderInterestSmsTemplateApiArg = z.infer<typeof renderInterestSmsTemplateApiArgSchema>;
export type RenderInterestSmsTemplateApiResponse = RenderSmsTemplateResponseBody;
export const renderInterestSmsTemplateExampleApiArgSchema = z.object({
  body: renderSmsTemplateRequestBodySchema,
});
export type RenderInterestSmsTemplateExampleApiArg = z.infer<
  typeof renderInterestSmsTemplateExampleApiArgSchema
>;
export type RenderInterestSmsTemplateExampleApiResponse = RenderSmsTemplateResponseBody;
export const renderWorkshopInterestEmailTemplateRequestBodySchema = z.object({
  subject: z.string(),
  body: z.string(),
  workshopInterestId: z.string(),
  locale: localeSchema,
});
export type RenderWorkshopInterestEmailTemplateRequestBody = z.infer<
  typeof renderWorkshopInterestEmailTemplateRequestBodySchema
>;
export const renderWorkshopInterestEmailTemplateApiArgSchema = z.object({
  body: renderWorkshopInterestEmailTemplateRequestBodySchema,
});
export type RenderWorkshopInterestEmailTemplateApiArg = z.infer<
  typeof renderWorkshopInterestEmailTemplateApiArgSchema
>;
export type RenderWorkshopInterestEmailTemplateApiResponse = RenderEmailTemplateResponseBody;
export const renderWorkshopInterestEmailTemplateExampleApiArgSchema = z.object({
  body: renderEmailTemplateRequestBodySchema,
});
export type RenderWorkshopInterestEmailTemplateExampleApiArg = z.infer<
  typeof renderWorkshopInterestEmailTemplateExampleApiArgSchema
>;
export type RenderWorkshopInterestEmailTemplateExampleApiResponse = RenderEmailTemplateResponseBody;
export const renderWorkshopInterestSmsTemplateRequestBodySchema = z.object({
  template: z.string(),
  workshopInterestId: z.string(),
  locale: localeSchema,
});
export type RenderWorkshopInterestSmsTemplateRequestBody = z.infer<
  typeof renderWorkshopInterestSmsTemplateRequestBodySchema
>;
export const renderWorkshopInterestSmsTemplateApiArgSchema = z.object({
  body: renderWorkshopInterestSmsTemplateRequestBodySchema,
});
export type RenderWorkshopInterestSmsTemplateApiArg = z.infer<
  typeof renderWorkshopInterestSmsTemplateApiArgSchema
>;
export type RenderWorkshopInterestSmsTemplateApiResponse = RenderSmsTemplateResponseBody;
export const renderWorkshopInterestSmsTemplateExampleApiArgSchema = z.object({
  body: renderSmsTemplateRequestBodySchema,
});
export type RenderWorkshopInterestSmsTemplateExampleApiArg = z.infer<
  typeof renderWorkshopInterestSmsTemplateExampleApiArgSchema
>;
export type RenderWorkshopInterestSmsTemplateExampleApiResponse = RenderSmsTemplateResponseBody;

export type CustomerMessageDefinitionResponseBody = {
  id: string;
  name: string;
  description: string;
  channels: Channel[];
  deliveryType: 'manual' | 'automatic';
  hasSettings: boolean;
  context: TemplateContextEnum;
};
export type GetCustomerMessageDefinitionApiResponse =
  /** status 200  */ CustomerMessageDefinitionResponseBody;
export type GetCustomerMessageDefinitionApiArg = {
  id: string;
  /** Authorization profile id */
  'X-Authorization-Profile'?: string;
  /** User JWT tokens are allowed */
  authorization?: string;
};

export type SchedulerLatestRunResponseBody = {
  name: string;
  scheduledFor: string;
  lastRanAt: string | null;
};

export type ListSchedulerLatestRunsResponseBody = {
  runs: SchedulerLatestRunResponseBody[];
};

export type ListLatestSchedulerRunsApiResponse =
  /** status 200  */ ListSchedulerLatestRunsResponseBody;
export type ListLatestSchedulerRunsApiArg = void;

export type DeleteLatestSchedulerRunApiResponse = /** status 204  */ void;
export type DeleteLatestSchedulerRunApiArg = {
  schedulerName: string;
};

export type RunSchedulerApiResponse = /** status 204  */ void;
export type RunSchedulerApiArg = {
  schedulerName: string;
};
export type RenderCustomerMessageEmailTemplateV2ApiArg = {
  customerMessageId: string;
  renderCustomerMessageRequestBodyV2: RenderCustomerMessageRequestBodyV2;
};
export type RenderCustomerMessageEmailTemplateV2ResponseBody = {
  subject: string;
  body: string;
};

export type BusinessCaseOnlineOfferPayloadRequestBodyV2 = {
  customerMessageId: 'business_case-online_offer';
  businessCaseId: string;
  vehicleId: string | null;
  photoId: string | null;
  sellerId: string;
  onlineOfferUri: string;
  body: string;
};
export type BusinessCaseBuyingOnlineOfferPayloadRequestBodyV2 = {
  customerMessageId: 'business_case-buying_online_offer';
  businessCaseId: string;
  offerId: string;
  vehicleId: string;
  photoId: string | null;
  sellerId: string;
  body: string;
};
export type BusinessCaseCombinedOnlineOfferPayloadRequestBodyV2 = {
  customerMessageId: 'business_case-combined_online_offer';
  businessCaseId: string;
  sellingVehicleId: string;
  buyingVehicleId: string;
  sellerId: string;
  sellingOnlineOfferUri: string;
  buyingOnlineOfferUri: string;
  body: string;
};
export type MessageRequestBodyV2 = {
  date: string;
  content: string;
};
export type BusinessCaseUnreadConversationPayloadRequestBodyV2 = {
  customerMessageId: 'business_case-unread_conversation';
  businessCaseId: string;
  vehicleId: string;
  sellerId: string;
  onlineOfferUri: string;
  messages: MessageRequestBodyV2[];
  moreMessagesCount: number;
};
export type GeneralCustomerMessagePayloadRequestBodyV2 = {
  customerMessageId: 'customer-message';
  subject: string;
  body: string;
};
export type AttachmentRequestBodyV2 = {
  name: string;
  url: string;
};
export type ServiceCheckInPayloadRequestBodyV2 = {
  customerMessageId: 'service-customer_check_in';
  serviceCaseId: string;
  digitalCertificateUrl: string;
  attachments: AttachmentRequestBodyV2[];
};
export type ServiceHandoverPayloadRequestBodyV2 = {
  customerMessageId: 'service-customer_handover';
  serviceCaseId: string;
  digitalCertificateUrl: string;
  attachments: AttachmentRequestBodyV2[];
};
export type ServiceOrderContextPayloadRequestBodyV2 = {
  serviceCaseId: string;
  serviceOrderId: string;
};
export type ServiceOrderContextOptionalPayloadRequestBodyV2 = {
  customerMessageId: 'workshop-service_order_completed' | 'workshop-service_order_planned';
  context: ServiceOrderContextPayloadRequestBodyV2 | null;
};
export type VehicleContextPayloadRequestBodyV2 = {
  vehicleId: string;
};
export type VehicleContextOptionalPayloadRequestBodyV2 = {
  customerMessageId:
    | 'vehicle-inspection_expiration_reminder'
    | 'vehicle-regular_service_inspection_reminder';
  context: VehicleContextPayloadRequestBodyV2 | null;
};

export type EmailContextTemplate = {
  subject: string;
  body: string;
};

export type TireSetContextPayloadRequestBodyV2 = {
  tireOrderId: string;
  tireSetId: string;
};
export type TireSetContextOptionalPayloadRequestBodyV2 = {
  customerMessageId: 'tire_set-storage_end_reminder';
  context: TireSetContextPayloadRequestBodyV2 | null;
  emailContextTemplate?: EmailContextTemplate | null;
};

export const previewCustomerMessagesSchema = z.enum([
  'tire_set-storage_end_reminder',
  'vehicle-regular_service_inspection_reminder',
  'vehicle-inspection_expiration_reminder',
  'workshop-service_order_planned',
  'workshop-service_order_completed',
  'business_case-reservation_confirmation',
  'business_case-reservation_expiring',
  'business_case-online_offer_expiring',
  'business_case-deposit_payment_request',
  'business_case-deposit_received',
  'business_case-balance_payment_request',
  'business_case-payment_received',
  'business_case-payment_overdue_reminder',
  'business_case-contract_signed',
  'complaint-received',
  'complaint-closed',
  'test_drive-scheduled',
  'test_drive-reminder',
  'interest-received',
  'interest-no_response_follow_up',
]);
export type PreviewCustomerMessages = z.infer<typeof previewCustomerMessagesSchema>;

export type BusinessCaseContextPayloadRequestBodyV2 = {
  businessCaseId: string;
};
export type BusinessCaseContextOptionalPayloadRequestBodyV2 = {
  customerMessageId:
    | 'business_case-reservation_confirmation'
    | 'business_case-reservation_expiring'
    | 'business_case-online_offer_expiring'
    | 'business_case-deposit_payment_request'
    | 'business_case-deposit_received'
    | 'business_case-balance_payment_request'
    | 'business_case-payment_received'
    | 'business_case-payment_overdue_reminder'
    | 'business_case-contract_signed'
    | 'complaint-received'
    | 'complaint-closed'
    | 'test_drive-scheduled'
    | 'test_drive-reminder';
  context: BusinessCaseContextPayloadRequestBodyV2 | null;
  emailContextTemplate?: EmailContextTemplate | null;
};
export type InterestContextPayloadRequestBodyV2 = {
  interestId: string;
};
export type InterestContextOptionalPayloadRequestBodyV2 = {
  customerMessageId: 'interest-received' | 'interest-no_response_follow_up';
  context: InterestContextPayloadRequestBodyV2 | null;
  emailContextTemplate?: EmailContextTemplate | null;
};
export type AiLeadBusinessCaseOnlineOfferContextOptionalPayloadRequestBodyV2 = {
  customerMessageId: 'ai_lead-business_case_online_offer';
  context: null;
};

export type AiLeadInterestOfferContextOptionalPayloadRequestBodyV2 = {
  customerMessageId: 'ai_lead-interest_offer';
  context: null;
};

export type RenderCustomerMessageRequestBodyV2 = {
  payload:
    | ({
        customerMessageId: 'tire_set-storage_end_reminder';
      } & TireSetContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'business_case-reservation_confirmation';
      } & BusinessCaseContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'business_case-reservation_expiring';
      } & BusinessCaseContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'business_case-online_offer_expiring';
      } & BusinessCaseContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'business_case-deposit_payment_request';
      } & BusinessCaseContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'business_case-deposit_received';
      } & BusinessCaseContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'business_case-balance_payment_request';
      } & BusinessCaseContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'business_case-payment_received';
      } & BusinessCaseContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'business_case-payment_overdue_reminder';
      } & BusinessCaseContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'business_case-contract_signed';
      } & BusinessCaseContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'complaint-received';
      } & BusinessCaseContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'complaint-closed';
      } & BusinessCaseContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'test_drive-scheduled';
      } & BusinessCaseContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'test_drive-reminder';
      } & BusinessCaseContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'interest-received';
      } & InterestContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'interest-no_response_follow_up';
      } & InterestContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'ai_lead-business_case_online_offer';
      } & AiLeadBusinessCaseOnlineOfferContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'ai_lead-interest_offer';
      } & AiLeadInterestOfferContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'business_case-online_offer';
      } & BusinessCaseOnlineOfferPayloadRequestBodyV2)
    | ({
        customerMessageId: 'business_case-buying_online_offer';
      } & BusinessCaseBuyingOnlineOfferPayloadRequestBodyV2)
    | ({
        customerMessageId: 'business_case-combined_online_offer';
      } & BusinessCaseCombinedOnlineOfferPayloadRequestBodyV2)
    | ({
        customerMessageId: 'business_case-unread_conversation';
      } & BusinessCaseUnreadConversationPayloadRequestBodyV2)
    | ({
        customerMessageId: 'customer-message';
      } & GeneralCustomerMessagePayloadRequestBodyV2)
    | ({
        customerMessageId: 'service-customer_check_in';
      } & ServiceCheckInPayloadRequestBodyV2)
    | ({
        customerMessageId: 'service-customer_handover';
      } & ServiceHandoverPayloadRequestBodyV2)
    | ({
        customerMessageId: 'workshop-service_order_completed';
      } & ServiceOrderContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'workshop-service_order_planned';
      } & ServiceOrderContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'vehicle-inspection_expiration_reminder';
      } & VehicleContextOptionalPayloadRequestBodyV2)
    | (({
        customerMessageId: 'vehicle-regular_service_inspection_reminder';
      } & VehicleContextOptionalPayloadRequestBodyV2) & {
        emailContextTemplate: EmailContextTemplate | null;
      });
  locale: string;
  customerId?: string | null;
  bodyFormat?: MessageContentFormat;
};

export const userMailboxProviderSchema = z.enum(['google', 'microsoft', 'imap']);
export type UserMailboxProvider = z.infer<typeof userMailboxProviderSchema>;

export const mailboxConnectionStatusSchema = z.enum([
  'CONNECTED',
  'CONNECTING',
  'ERROR',
  'AUTH_ERROR',
]);
export type MailboxConnectionStatus = z.infer<typeof mailboxConnectionStatusSchema>;

export const createUserOAuthMailboxRequestBodySchema = z.object({
  name: z.string(),
  redirectUri: z.string(),
});
export type CreateUserOAuthMailboxRequestBody = z.infer<
  typeof createUserOAuthMailboxRequestBodySchema
>;

export const createdOAuthMailboxResponseBodySchema = z.object({
  id: z.string(),
  oauthUrl: z.string(),
});
export type CreatedOAuthMailboxResponseBody = z.infer<typeof createdOAuthMailboxResponseBodySchema>;

export type CreateGoogleUserMailboxApiResponse = /** status 201  */ CreatedOAuthMailboxResponseBody;
export type CreateGoogleUserMailboxApiArg = {
  createUserGoogleMailboxRequestBody: CreateUserOAuthMailboxRequestBody;
};
export type CreateMicrosoftUserMailboxApiResponse =
  /** status 201  */ CreatedOAuthMailboxResponseBody;
export type CreateMicrosoftUserMailboxApiArg = {
  createUserMicrosoftMailboxRequestBody: CreateUserOAuthMailboxRequestBody;
};

export const createUserImapMailboxRequestBodySchema = z.object({
  name: z.string(),
  host: z.string(),
  port: z.number(),
  username: z.string(),
  password: z.string(),
  secure: z.boolean(),
});
export type CreateUserImapMailboxRequestBody = z.infer<
  typeof createUserImapMailboxRequestBodySchema
>;
export type UpdateUserImapMailboxRequestBody = CreateUserImapMailboxRequestBody;

export type CreateImapUserMailboxApiResponse = /** status 201  */ UuidResponseBody;
export type CreateImapUserMailboxApiArg = {
  createUserImapMailboxRequestBody: CreateUserImapMailboxRequestBody;
};
export type UpdateImapUserMailboxApiResponse = /** status 204  */ void;
export type UpdateImapUserMailboxApiArg = {
  mailboxId: string;
  updateUserImapMailboxRequestBody: UpdateUserImapMailboxRequestBody;
};
export type DeleteUserMailboxApiResponse = /** status 204  */ void;
export type DeleteUserMailboxApiArg = {
  mailboxId: string;
};

export const userMailboxResponseBodySchema = z.object({
  id: z.string(),
  name: z.string(),
  accountId: z.string(),
  type: userMailboxProviderSchema,
  createdAt: z.string(),
  email: z.string().nullable(),
  status: mailboxConnectionStatusSchema,
});
export type UserMailboxResponseBody = z.infer<typeof userMailboxResponseBodySchema>;

export const listUserMailboxesResponseBodySchema = z.object({
  mailboxes: z.array(userMailboxResponseBodySchema),
});
export type ListUserMailboxesResponseBody = z.infer<typeof listUserMailboxesResponseBodySchema>;

export type ListUserMailboxesApiResponse = /** status 200  */ ListUserMailboxesResponseBody;
export type ListUserMailboxesApiArg = void;

export const reauthenticateUserMailboxRequestBodySchema = z.object({
  redirectUri: z.string(),
});
export type ReauthenticateUserMailboxRequestBody = z.infer<
  typeof reauthenticateUserMailboxRequestBodySchema
>;

export const reauthenticatedMailboxResponseBodySchema = z.object({
  oauthUrl: z.string(),
});
export type ReauthenticatedMailboxResponseBody = z.infer<
  typeof reauthenticatedMailboxResponseBodySchema
>;

export type ReauthenticateUserMailboxApiResponse =
  /** status 200  */ ReauthenticatedMailboxResponseBody;
export type ReauthenticateUserMailboxApiArg = {
  mailboxId: string;
  reauthenticateUserMailboxRequestBody: ReauthenticateUserMailboxRequestBody;
};

export const tenantMailboxResponseBodySchema = z.object({
  id: z.string(),
  name: z.string(),
  accountId: z.string(),
  type: userMailboxProviderSchema,
  createdAt: z.string(),
  email: z.string().nullable(),
  status: mailboxConnectionStatusSchema,
});
export type TenantMailboxResponseBody = z.infer<typeof tenantMailboxResponseBodySchema>;

export const listTenantMailboxesResponseBodySchema = z.object({
  mailboxes: z.array(tenantMailboxResponseBodySchema),
});
export type ListTenantMailboxesResponseBody = z.infer<typeof listTenantMailboxesResponseBodySchema>;

export type ListTenantMailboxesApiResponse = /** status 200  */ ListTenantMailboxesResponseBody;
export type ListTenantMailboxesApiArg = void;

export type CreateGoogleTenantMailboxApiResponse =
  /** status 201  */ CreatedOAuthMailboxResponseBody;
export type CreateGoogleTenantMailboxApiArg = {
  createTenantGoogleMailboxRequestBody: CreateUserOAuthMailboxRequestBody;
};
export type CreateMicrosoftTenantMailboxApiResponse =
  /** status 201  */ CreatedOAuthMailboxResponseBody;
export type CreateMicrosoftTenantMailboxApiArg = {
  createTenantMicrosoftMailboxRequestBody: CreateUserOAuthMailboxRequestBody;
};

export type CreateImapTenantMailboxApiResponse = /** status 201  */ UuidResponseBody;
export type CreateImapTenantMailboxApiArg = {
  createTenantImapMailboxRequestBody: CreateUserImapMailboxRequestBody;
};

export type DeleteTenantMailboxApiResponse = /** status 204  */ void;
export type DeleteTenantMailboxApiArg = {
  mailboxId: string;
};

export type ReauthenticateTenantMailboxApiResponse =
  /** status 200  */ ReauthenticatedMailboxResponseBody;
export type ReauthenticateTenantMailboxApiArg = {
  mailboxId: string;
  reauthenticateTenantMailboxRequestBody: ReauthenticateUserMailboxRequestBody;
};

export const customerMessageDirectionSchema = z.enum(['INCOMING', 'OUTGOING']);
export type CustomerMessageDirection = z.infer<typeof customerMessageDirectionSchema>;

export const customerMessageChannelSchema = z.enum(['email', 'sms', 'whatsapp', 'call']);
export type CustomerMessageChannel = z.infer<typeof customerMessageChannelSchema>;

export const customerMessageStateSchema = z.enum(['blocked', 'created', 'failed', 'sent']);
export type CustomerMessageState = z.infer<typeof customerMessageStateSchema>;

export const customerMessageAttachmentResponseBodySchema = z.object({
  filename: z.string(),
  uri: z.string(),
  contentType: z.string().nullable(),
  size: z.number().nullable(),
});
export type CustomerMessageAttachmentResponseBody = z.infer<
  typeof customerMessageAttachmentResponseBodySchema
>;

export const customerMessageSecondaryRecordResponseBodySchema = z.object({
  recordId: z.string(),
  resourceId: z.string(),
});
export type CustomerMessageSecondaryRecordResponseBody = z.infer<
  typeof customerMessageSecondaryRecordResponseBodySchema
>;

export const customerMessageCallOutcomeSchema = z.enum([
  'connected',
  'nobody_answered',
  'caller_hung_up',
  'customer_not_reached',
  'bot',
]);
export type CustomerMessageCallOutcome = z.infer<typeof customerMessageCallOutcomeSchema>;

export const customerMessageCallResponseBodySchema = z.object({
  id: z.string(),
  outcome: customerMessageCallOutcomeSchema,
  durationSeconds: z.number().nullable(),
  line: z.object({
    id: z.string(),
    name: z.string(),
    phoneNumber: z.string(),
  }),
  user: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  note: z
    .string()
    .nullish()
    .transform((note) => note ?? null),
  /**
   * A backend without call recording leaves it out: nothing to show. The same shape as
   * `TelephonyCallRecordingAvailabilitySchema` in `@dms/api/settings`, kept as a copy because API
   * groups share only primitives.
   */
  recording: z
    .object({
      status: z.enum(['available', 'processing', 'failed', 'expired', 'none']),
      durationSeconds: z.number().nullable(),
      availableUntil: z.string().nullable(),
      expiredAt: z.string().nullable(),
    })
    .nullish()
    .transform((recording) => recording ?? null),
});
export type CustomerMessageCallResponseBody = z.infer<typeof customerMessageCallResponseBodySchema>;

export const customerMessageResponseBodySchema = z.object({
  id: z.string(),
  customerId: z.string().nullable(),
  senderId: z.string().nullable(),
  direction: customerMessageDirectionSchema,
  channel: customerMessageChannelSchema,
  state: customerMessageStateSchema.nullish(),
  readAt: z.string().nullable(),
  // Shared read state of the communication, the same for every user; always false for outgoing.
  // Defaults to false where a response is parsed with this schema (e.g. the service case thread).
  // listCustomerMessages does not parse its response; its consumers treat a missing value as false.
  isUnread: z.boolean().default(false),
  contact: z.string(),
  content: z.string(),
  subject: z.string().nullable(),
  createdAt: z.string(),
  attachments: z.array(customerMessageAttachmentResponseBodySchema),
  recordId: z.string().nullable(),
  resourceId: z.string().nullable(),
  secondaryRecords: z.array(customerMessageSecondaryRecordResponseBodySchema),
  emailMessageId: z.string().nullable(),
  parentEmailMessageId: z.string().nullable(),
  conversationId: z.string().nullable(),
  senderAddress: z.string().nullable(),
  recipientAddresses: z.array(z.string()),
  // Set only on call rows; nullish so rows from a backend without calls still parse.
  call: customerMessageCallResponseBodySchema.nullish(),
});
export type CustomerMessageResponseBody = z.infer<typeof customerMessageResponseBodySchema>;

export const listCustomerMessagesResponseBodySchema = z.object({
  messages: z.array(customerMessageResponseBodySchema),
});
export type ListCustomerMessagesResponseBody = z.infer<
  typeof listCustomerMessagesResponseBodySchema
>;

export type ListCustomerMessagesApiResponse = /** status 200  */ ListCustomerMessagesResponseBody;
export type ListCustomerMessagesApiArg = {
  customerId: string;
  limit?: number;
  offset?: number;
  recordId?: string;
  resourceId?: EntityResourceIds;
};

export type AssignSyncedMessageCustomerApiResponse = void;
export type AssignSyncedMessageCustomerApiArg = {
  syncedMessageId: string;
  customerId: string;
};

export type UnassignSyncedMessageCustomerApiResponse = void;
export type UnassignSyncedMessageCustomerApiArg = {
  syncedMessageId: string;
};

export type AssignSyncedMessageRecordApiResponse = void;
export type AssignSyncedMessageRecordApiArg = {
  syncedMessageId: string;
  recordId: RecordIdHttpModel;
};

export type UnassignSyncedMessageRecordApiResponse = void;
export type UnassignSyncedMessageRecordApiArg = {
  syncedMessageId: string;
};

export type MarkSyncedMessagesAsReadApiResponse = void;
export type MarkSyncedMessagesAsReadApiArg = {
  receivedBefore: string;
  recordId?: RecordIdHttpModel;
};

export const communicationIdsRequestBodySchema = z.object({
  communicationIds: z.array(z.string()),
});
export type CommunicationIdsRequestBody = z.infer<typeof communicationIdsRequestBodySchema>;

export type MarkCommunicationsAsOpenedApiResponse = void;
/** Only `communicationIds` is sent; the rest narrows which lists and counts refetch afterwards. */
type CommunicationReadStateApiArg = CommunicationIdsRequestBody & {
  customerId?: string;
  resourceId?: string;
  recordId?: string;
};

export type MarkCommunicationsAsOpenedApiArg = CommunicationReadStateApiArg;

export type MarkCommunicationsAsReadApiResponse = void;
export type MarkCommunicationsAsReadApiArg = CommunicationReadStateApiArg;

export type MarkCommunicationsAsUnreadApiResponse = void;
export type MarkCommunicationsAsUnreadApiArg = CommunicationReadStateApiArg;

export const unreadCommunicationCountResponseBodySchema = z.object({
  count: z.number().int().nonnegative(),
});
export type UnreadCommunicationCountResponseBody = z.infer<
  typeof unreadCommunicationCountResponseBodySchema
>;

export const unreadCommunicationRecordResourceIdSchema = z.enum(['INTEREST', 'BUSINESS_CASE']);
export type UnreadCommunicationRecordResourceId = z.infer<
  typeof unreadCommunicationRecordResourceIdSchema
>;

export type GetRecordUnreadCommunicationCountApiResponse = UnreadCommunicationCountResponseBody;
export type GetRecordUnreadCommunicationCountApiArg = {
  resourceId: UnreadCommunicationRecordResourceId;
  recordId: string;
};

export type GetUnreadCommunicationCountApiResponse = UnreadCommunicationCountResponseBody;
export type GetUnreadCommunicationCountApiArg = void;
